import * as React from 'react';
import { useRef } from 'react';

import { cn } from '../../../utils.js';
import { cva, type VariantProps } from 'class-variance-authority';
import { Label } from '../label.js';
import { Text, textVariants } from '../../Text.js';
import { HStack } from '../../HStack.js';
import { Command as CommandPrimitive } from 'cmdk';

const inputVariants = cva(
  'flex w-full font-body rounded-md text-lg bg-gray-50 hover:bg-gray-100 active:bg-gray-100 focus-within:outline focus-within:outline-gray-200 placeholder:text-black placeholder:text-opacity-40 placeholder:text-sm disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'text-black',
        error: 'text-red-400',
      },
      textarea: {
        true: 'h-auto p-4',
        false: 'h-12 px-2 py-[10px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      textarea: false,
    },
  }
);

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    Omit<
      React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
      'value'
    >,
    VariantProps<typeof inputVariants> {
  label?: React.ReactNode;
  error?: React.ReactNode;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, type, label, error, leftElement, rightElement, ...props },
    ref
  ) => {
    const id = useRef(Math.random());
    return (
      <div className='grid w-full items-center gap-1 p-[2px]'>
        {label && (
          <Label
            htmlFor={props.id ?? id.current.toString()}
            className={textVariants({
              font: 'regular-xs',
              color: error ? 'red' : 'gray-500',
            })}
          >
            {label}
          </Label>
        )}
        <HStack
          className={cn(
            inputVariants({
              variant: error ? 'error' : 'default',
              className: 'flex-grow items-center gap-2',
            }),
            className
          )}
        >
          {leftElement && leftElement}
          <input
            type={type}
            id={props.id ?? id.current.toString()}
            className='w-full bg-transparent focus:outline-none'
            ref={ref}
            {...props}
          />
          {rightElement && rightElement}
        </HStack>
        {error !== true && error && (
          <Text color={'red'} font={'regular-xs'}>
            {error}
          </Text>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input, inputVariants };
